import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, Check, Loader2, X } from "lucide-react";

export default function UploadPortal() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [templates, setTemplates] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contract-templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data || []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const uploadAll = async () => {
    if (files.length === 0) { toast.error("Select files to upload"); return; }
    setUploading(true);
    let success = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/contract-templates/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        success++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    toast.success(`${success} of ${files.length} files uploaded`);
    setFiles([]);
    setUploading(false);
    fetchTemplates();
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/contract-templates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Template removed");
      fetchTemplates();
    } catch { toast.error("Failed to delete"); }
  };

  const downloadTemplate = async (tpl) => {
    try {
      const res = await axios.get(`${API}/contract-templates/${tpl.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = tpl.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const EXT_ICONS = { pdf: "#E24B4A", doc: "#185FA5", docx: "#185FA5", xls: "#1a7a52", xlsx: "#1a7a52", txt: "#888" };

  return (
    <div data-testid="upload-portal">
      <div style={{ background: "var(--surface)", borderBottom: "0.5px solid var(--border)", padding: "16px 24px" }}>
        <div style={{ fontSize: 22, fontWeight: 500, color: "var(--text-primary)" }}>Contract Template Upload</div>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>Upload agreement files to use as contract templates.</div>
      </div>

      <div className="page-content" style={{ padding: "24px", maxWidth: 800 }}>
        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => document.getElementById("file-input").click()}
          style={{
            border: `2px dashed ${dragOver ? "var(--blue)" : "var(--border)"}`,
            borderRadius: 12,
            padding: "40px 24px",
            textAlign: "center",
            cursor: "pointer",
            background: dragOver ? "var(--blue-light)" : "var(--surface)",
            transition: "all 0.15s",
            marginBottom: 20,
          }}
          data-testid="drop-zone"
        >
          <Upload style={{ width: 32, height: 32, color: dragOver ? "var(--blue)" : "var(--text-tertiary)", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", marginBottom: 4 }}>
            Drop files here or click to browse
          </div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
            PDF, DOCX, DOC, TXT — upload as many as you need
          </div>
          <input
            id="file-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
            onChange={e => handleFiles(e.target.files)}
            style={{ display: "none" }}
            data-testid="file-input"
          />
        </div>

        {/* Selected files */}
        {files.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>
              Selected files ({files.length})
            </div>
            {files.map((f, i) => {
              const ext = f.name.split(".").pop().toLowerCase();
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "var(--surface)", border: "0.5px solid var(--border)", borderRadius: 8, marginBottom: 6 }} data-testid={`selected-file-${i}`}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--gray-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FileText style={{ width: 16, height: 16, color: EXT_ICONS[ext] || "var(--text-tertiary)" }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{fmtSize(f.size)} · {ext.toUpperCase()}</div>
                  </div>
                  <button onClick={() => removeFile(i)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                    <X style={{ width: 16, height: 16, color: "var(--text-tertiary)" }} />
                  </button>
                </div>
              );
            })}
            <button
              onClick={uploadAll}
              disabled={uploading}
              className="btn btn-primary"
              style={{ fontSize: 13, padding: "10px 24px", width: "100%", marginTop: 8, justifyContent: "center", opacity: uploading ? 0.6 : 1 }}
              data-testid="upload-btn"
            >
              {uploading ? <><Loader2 style={{ width: 14, height: 14, animation: "spin 0.8s linear infinite" }} /> Uploading...</> : <><Upload style={{ width: 14, height: 14 }} /> Upload {files.length} file{files.length > 1 ? "s" : ""}</>}
            </button>
          </div>
        )}

        {/* Uploaded templates */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 10 }}>
            Uploaded templates ({templates.length})
          </div>
          {templates.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)", border: "0.5px dashed var(--border)", borderRadius: 10 }}>
              <FileText style={{ width: 28, height: 28, margin: "0 auto 8px", opacity: 0.3 }} />
              <div style={{ fontSize: 13 }}>No templates uploaded yet</div>
            </div>
          )}
          {templates.map(tpl => {
            const ext = (tpl.original_filename || "").split(".").pop().toLowerCase();
            return (
              <div key={tpl.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", border: "0.5px solid var(--border)", borderRadius: 8, marginBottom: 6 }} data-testid={`template-${tpl.id}`}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--green-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Check style={{ width: 16, height: 16, color: "var(--green)" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{tpl.original_filename}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                    {fmtSize(tpl.size)} · {ext.toUpperCase()} · Uploaded {tpl.created_at ? new Date(tpl.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                    {tpl.uploaded_by && ` by ${tpl.uploaded_by}`}
                  </div>
                </div>
                <button onClick={() => downloadTemplate(tpl)} style={{ background: "none", border: "0.5px solid var(--border)", borderRadius: 8, padding: "5px 8px", cursor: "pointer" }} title="Download">
                  <Download style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} />
                </button>
                <button onClick={() => deleteTemplate(tpl.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }} title="Remove">
                  <Trash2 style={{ width: 14, height: 14, color: "var(--red)" }} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
